import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, MessageCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function Books() {
  const { content } = useSite();
  const [books, setBooks] = useState([]);

  useEffect(() => {
    api.get("/books").then(({ data }) => setBooks(data)).catch(() => {});
  }, []);

  const wa = content ? content.whatsapp : "33757829980";
  const orderLink = (b) =>
    `https://wa.me/${wa}?text=${encodeURIComponent(`Bonjour, je souhaite commander le livre « ${b.title} » (${b.price || "prix à confirmer"}).`)}`;

  return (
    <div data-testid="books-page" className="pt-32 pb-16 px-5 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-6">
          <BookOpen className="text-amber-300" size={26} />
        </div>
        <p className="text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-4">Bibliothèque</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-5">Livres</h1>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto">Des ouvrages pour approfondir votre chemin intérieur.</p>
      </div>

      {books.length === 0 ? (
        <p className="text-center text-slate-500">Les livres seront bientôt disponibles.</p>
      ) : (
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {books.map((b, i) => (
            <motion.div
              key={b.id}
              {...fade}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="glass card-hover rounded-2xl overflow-hidden flex flex-col"
              data-testid={`book-card-${b.id}`}
            >
              <div className="aspect-[3/4] bg-gradient-to-br from-amber-900/20 to-black flex items-center justify-center overflow-hidden">
                {b.has_cover ? (
                  <img src={`${API}/books/${b.id}/cover`} alt={b.title} className="w-full h-full object-cover" />
                ) : (
                  <BookOpen className="text-amber-500/40" size={64} />
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-amber-100 mb-1">{b.title}</h3>
                {b.author && <p className="text-xs font-mono-accent text-amber-400/70 mb-3">{b.author}</p>}
                <p className="text-slate-400 text-sm leading-relaxed flex-1">{b.description}</p>
                <div className="flex items-center justify-between mt-5">
                  {b.price && <span className="font-mono-accent text-xl gold-text">{b.price}</span>}
                  <a
                    href={orderLink(b)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`book-order-${b.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.03] transition-transform"
                  >
                    <MessageCircle size={15} /> Commander
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
